import React, { useEffect, useState, useRef } from "react";
import { FaGift, FaEdit, FaTrash } from "react-icons/fa";
import { FaSortAmountUpAlt } from "react-icons/fa";
import { MdEmojiEmotions } from "react-icons/md";
import { HiSearch, HiX } from "react-icons/hi";
import Switch from "@mui/material/Switch";
import Widget from "Components/Common/Widget/Widget";
import { getAllEmojis } from "Common Usage/apiService";
import { loadDecrypted } from "Common Usage/localStorageUtils";
import AddCategoryModal from "./components/AddCategoryModal";
import CategorySection from "./components/CategorySection";
import AddEmojiGiftForm from "./components/AddEmojiGiftForm";
import EmojiWarningPopup from "./components/EmojiWarningPopup";
import EmojiEditPopup from "./components/EmojiEditPopup";
import { usePermission } from "Context/PermissionContext";
import axios from "axios";
import Pagination from "Components/Pagination/Pagination";

const TABS = { CATEGORIES: "categories", GIFTS: "gifts" };

const Gifts = () => {
  const [allEmojis, setAllEmojis] = useState([]);
  const [flatEmojis, setFlatEmojis] = useState([]);
  const [emojiDataLoading, setEmojiDataLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoryModal, setCategoryModal] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.CATEGORIES);

  // Gifts tab state
  const [addEmojiModal, setAddEmojiModal] = useState(false);
  const [targetCategoryId, setTargetCategoryId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");
  const searchInputRef = useRef(null);

  const loggedInUser = loadDecrypted("HeartConnect-StaffData");
  const staffData = Array.isArray(loggedInUser)
    ? loggedInUser[0]
    : loggedInUser;
  const staffId = staffData?._id || staffData?.id || "";

  // Pagination states
  const [categoryPage, setCategoryPage] = useState(1);
  const [giftPage, setGiftPage] = useState(1);

  const LIMIT = 8;

  useEffect(() => {
    fetchAllEmojis();
    fetchCategories();
  }, []);

  useEffect(() => {
    setGiftPage(1);
  }, [searchQuery, selectedCategoryFilter]);

  useEffect(() => {
    setCategoryPage(1);
    setGiftPage(1);
  }, [activeTab]);

  const fetchAllEmojis = async () => {
    try {
      const data = await getAllEmojis(true);

      if (!Array.isArray(data)) return;

      if (data[0]?.gifts) {
        // Grouped format: [{ categoryId, categoryName, gifts: [...] }]
        setAllEmojis(data);
        setFlatEmojis(data.flatMap((cat) => cat.gifts));
      } else {
        // Flat format: [{ _id, name, emoji, category: { _id, name }, ... }]
        const grouped = {};
        data.forEach((item) => {
          const catId = item.category?._id || item.category || "uncategorized";
          const catName = item.category?.name || "Uncategorized";
          if (!grouped[catId])
            grouped[catId] = {
              categoryId: catId,
              categoryName: catName,
              gifts: [],
            };
          grouped[catId].gifts.push(item);
        });
        setAllEmojis(Object.values(grouped));
        setFlatEmojis(data);
      }
    } catch (error) {
      console.log("getAllEmojis-Error", error.message);
    } finally {
      setEmojiDataLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/emoji-categories/get-categories?isAdmin=true`
      );
      setCategories(res.data?.data || res.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const handleCategoryToggle = async (cat) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/emoji-categories/toggle-category/${cat._id}`
      );
      fetchCategories();
    } catch (err) {
      console.error("Category toggle failed:", err);
    }
  };

  const handleOpenAddEmoji = (categoryId) => {
    setTargetCategoryId(categoryId);
    setAddEmojiModal(true);
  };

  const handleSearchClear = () => setSearchQuery("");

  // Gifts tab: filter by category + search
  const filteredGifts = flatEmojis
    .filter((item) => {
      const matchesCat = selectedCategoryFilter
        ? (item.category?._id || item.category) === selectedCategoryFilter
        : true;
      const matchesSearch = searchQuery.trim()
        ? item.name?.toLowerCase().includes(searchQuery.trim().toLowerCase())
        : true;
      return matchesCat && matchesSearch;
    })
    .map((item) => {
      const group = allEmojis.find((g) =>
        g.gifts?.some((g2) => g2._id === item._id)
      );
      return {
        ...item,
        categoryName:
          group?.categoryName || item.category?.name || "Uncategorized",
      };
    });

  const isFiltering = searchQuery.trim().length > 0 || selectedCategoryFilter;

  const paginatedCategories = categories.slice(
    (categoryPage - 1) * LIMIT,
    categoryPage * LIMIT
  );

  const totalCategoryPages = Math.ceil(categories.length / LIMIT);

  const paginatedGifts = filteredGifts.slice(
    (giftPage - 1) * LIMIT,
    giftPage * LIMIT
  );

  const totalGiftPages = Math.ceil(filteredGifts.length / LIMIT);

  return (
    <React.Fragment>
      {/* ── Emoji add modal ── */}
      <AddEmojiGiftForm
        addEmojiModal={addEmojiModal}
        handleAddModalClose={() => {
          setAddEmojiModal(false);
          setTargetCategoryId(null);
        }}
        fetchAllEmojis={fetchAllEmojis}
        staffId={staffId}
        categories={categories}
        fetchCategories={fetchCategories}
        preselectedCategoryId={targetCategoryId}
      />

      {/* ── Add Category modal ── */}
      <AddCategoryModal
        open={categoryModal}
        handleClose={() => setCategoryModal(false)}
        fetchCategories={fetchCategories}
      />

      {/* ── Widgets ── */}
      <div className="mt-3 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-6">
        <Widget
          icon={<FaGift className="h-6 w-6" />}
          title="Total No. of Gifts"
          subtitle={flatEmojis.length}
        />
        <Widget
          icon={<MdEmojiEmotions className="h-6 w-6" />}
          title="Total Categories"
          subtitle={categories.length}
        />
        <Widget
          icon={<FaSortAmountUpAlt className="h-6 w-6" />}
          title="Active Gifts"
          subtitle={flatEmojis.filter((g) => g.isActive).length}
        />
      </div>

      {/* ── Tab Container ── */}
      <div className="mt-4 overflow-hidden rounded-2xl bg-white shadow-sm">
        {/* Tab Header */}
        <div className="flex border-b border-gray-100 px-1">
          <button
            onClick={() => setActiveTab(TABS.CATEGORIES)}
            className={`relative flex items-center gap-2 px-6 py-4 text-sm font-semibold transition-colors duration-200
              ${
                activeTab === TABS.CATEGORIES
                  ? "text-[#c51369]"
                  : "text-gray-400 hover:text-gray-600"
              }`}
          >
            🗂️ Categories
            <span className="rounded-full bg-pink-50 px-2 py-0.5 text-[11px] font-bold text-[#c51369]">
              {categories.length}
            </span>
            {activeTab === TABS.CATEGORIES && (
              <span className="absolute bottom-0 left-0 right-0 h-[2.5px] rounded-t-sm bg-[#c51369]" />
            )}
          </button>

          <button
            onClick={() => setActiveTab(TABS.GIFTS)}
            className={`relative flex items-center gap-2 px-6 py-4 text-sm font-semibold transition-colors duration-200
              ${
                activeTab === TABS.GIFTS
                  ? "text-[#c51369]"
                  : "text-gray-400 hover:text-gray-600"
              }`}
          >
            🎁 Emoji Gifts
            <span className="rounded-full bg-pink-50 px-2 py-0.5 text-[11px] font-bold text-[#c51369]">
              {flatEmojis.length}
            </span>
            {activeTab === TABS.GIFTS && (
              <span className="absolute bottom-0 left-0 right-0 h-[2.5px] rounded-t-sm bg-[#c51369]" />
            )}
          </button>
        </div>

        {/* ── CATEGORIES TAB ── */}
        {activeTab === TABS.CATEGORIES && (
          <div className="p-5">
            {/* Toolbar */}
            <div className="mb-5 flex items-center justify-end">
              <button
                onClick={() => setCategoryModal(true)}
                className="rounded-lg bg-[#c51369] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a10f5b]"
              >
                + Add New Category
              </button>
            </div>

            {categories.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 py-16 text-gray-400">
                <span className="mb-2 text-4xl">🗂️</span>
                <p className="text-sm">
                  No categories yet. Create one to get started.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {paginatedCategories.map((cat) => {
                  const group = allEmojis.find((g) => g.categoryId === cat._id);
                  return (
                    <CategorySection
                      key={cat._id}
                      category={cat}
                      group={group}
                      onCategoryToggle={handleCategoryToggle}
                      onAddEmoji={() => handleOpenAddEmoji(cat._id)}
                      fetchAllEmojis={fetchAllEmojis}
                      categories={categories}
                      staffId={staffId}
                    />
                  );
                })}
              </div>
            )}

            {/* Pagination for Categories */}
            {totalCategoryPages > 1 && (
              <Pagination
                currentPage={categoryPage}
                totalPages={totalCategoryPages}
                onPageChange={setCategoryPage}
              />
            )}
          </div>
        )}

        {/* ── GIFTS TAB ── */}
        {activeTab === TABS.GIFTS && (
          <div className="p-5">
            {/* Toolbar */}
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-1 flex-wrap items-center gap-3">
                {/* Search */}
                <div
                  className="flex min-w-[200px] max-w-xs flex-1 items-center gap-2 rounded-lg border
                    px-3 py-2 transition-all focus-within:border-[#c51369]"
                >
                  <HiSearch size={16} className="shrink-0 text-[#c51369]" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search emoji gifts..."
                    className="bg-transparent flex-1 text-sm text-gray-700 placeholder:text-[#c51369]/50 focus:outline-none"
                  />
                  {searchQuery && (
                    <button
                      onClick={handleSearchClear}
                      className="shrink-0 text-gray-400 transition-colors hover:text-[#c51369]"
                    >
                      <HiX size={15} />
                    </button>
                  )}
                </div>

                {/* Category filter */}
                <select
                  value={selectedCategoryFilter}
                  onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                  className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700
                    transition-all focus:border-[#c51369] focus:outline-none focus:ring-0"
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => handleOpenAddEmoji(null)}
                className="shrink-0 rounded-lg bg-[#c51369] px-5 py-2 text-sm font-semibold text-white
                  transition-colors hover:bg-[#a10f5b]"
              >
                + Add New Gift
              </button>
            </div>

            {/* Result meta */}
            {isFiltering && (
              <p className="mb-4 text-sm text-gray-500">
                {filteredGifts.length > 0 ? (
                  <>
                    Showing{" "}
                    <span className="font-semibold text-gray-700">
                      {filteredGifts.length}
                    </span>{" "}
                    gift{filteredGifts.length !== 1 ? "s" : ""}
                    {selectedCategoryFilter && (
                      <>
                        {" "}
                        in{" "}
                        <span className="font-semibold text-[#c51369]">
                          {
                            categories.find(
                              (c) => c._id === selectedCategoryFilter
                            )?.name
                          }
                        </span>
                      </>
                    )}
                    {searchQuery.trim() && (
                      <>
                        {" "}
                        matching{" "}
                        <span className="font-semibold text-[#c51369]">
                          "{searchQuery}"
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    No results
                    {searchQuery.trim() && (
                      <>
                        {" "}
                        for{" "}
                        <span className="font-semibold text-[#c51369]">
                          "{searchQuery}"
                        </span>
                      </>
                    )}
                  </>
                )}
                <button
                  onClick={() => {
                    handleSearchClear();
                    setSelectedCategoryFilter("");
                  }}
                  className="ml-3 text-xs text-gray-400 underline underline-offset-2 hover:text-[#c51369]"
                >
                  Clear filters
                </button>
              </p>
            )}

            {/* Gifts grid */}
            {emojiDataLoading ? (
              <GiftsSkeletonLoader />
            ) : filteredGifts.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 py-16 text-gray-400">
                <span className="mb-2 text-4xl">🔍</span>
                <p className="text-sm">
                  {isFiltering
                    ? "No emoji gifts match your filters."
                    : "No emoji gifts yet."}
                </p>
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {paginatedGifts.map((item, index) => (
                  <GiftCard
                    key={item._id}
                    item={item}
                    index={index}
                    fetchAllEmojis={fetchAllEmojis}
                    categories={categories}
                    staffId={staffId}
                    giftPage={giftPage}
                    setGiftPage={setGiftPage}
                    totalGiftPages={totalGiftPages}
                  />
                ))}
              </div>
            )}

            {/* Pagination for Gifts */}
            {totalGiftPages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={giftPage}
                  totalPages={totalGiftPages}
                  onPageChange={setGiftPage}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

// ── Gift Card (used in Gifts tab) — with edit / delete / toggle ──
const gradientColors = [
  "from-teal-400 to-cyan-600",
  "from-amber-400 to-orange-600",
  "from-lime-400 to-green-600",
  "from-pink-400 to-rose-600",
  "from-blue-400 to-purple-600",
];
const GiftCard = ({ item, index, fetchAllEmojis, categories, staffId }) => {
  const [warnPopupOpen, setWarnPopupOpen] = useState(false);
  const [editEmojiModalOpen, setEditEmojiModalOpen] = useState(false);
  const [isActive, setIsActive] = useState(item.isActive);

  // Keep local state in sync if parent refetches and passes new item
  useEffect(() => {
    setIsActive(item.isActive);
  }, [item.isActive]);

  const { permissions } = usePermission();
  const currentTabPermissions =
    permissions?.coinManagement?.subModule?.manageGifts?.permissions;

  const handleToggleEmojiStatus = async () => {
    const newStatus = !isActive;
    setIsActive(newStatus); // optimistic update
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/emoji-package/toggle-emoji-packages/${item._id}`
      );
      // fetchAllEmojis();
    } catch (error) {
      console.error("Toggle failed:", error);
      setIsActive(!newStatus);
    }
  };

  const handleFeaturedToggle = async () => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/emoji-package/toggle-emoji-packages-featured/${item._id}`
      );
      await fetchAllEmojis();
    } catch (error) {
      console.log("Error in handleFeaturedToggle: ", error);
    }
  };

  return (
    <>
      <div
        className={`relative flex flex-col items-center justify-center rounded-2xl bg-gradient-to-tr
          ${
            gradientColors[index % gradientColors.length]
          } p-6 text-white shadow-md
          transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg`}
      >
        {/* Category badge — top left */}
        <span className="absolute left-3 top-3 rounded-full bg-white/25 px-2 py-0.5 text-[10px] font-semibold tracking-wide backdrop-blur-sm">
          {item.categoryName}
        </span>

        {/* Edit / Delete / Toggle — all inline, top right */}
        <div className="absolute right-2 top-2 flex items-center gap-2">
          {currentTabPermissions?.edit && (
            <button
              onClick={() => setEditEmojiModalOpen(true)}
              className="transition-opacity hover:opacity-75"
              title="Edit"
            >
              <FaEdit size={17} className="text-white hover:text-yellow-200" />
            </button>
          )}
          {currentTabPermissions?.delete && (
            <button
              onClick={() => setWarnPopupOpen(true)}
              className="transition-opacity hover:opacity-75"
              title="Delete"
            >
              <FaTrash size={16} className="text-white hover:text-red-200" />
            </button>
          )}
          <Switch
            checked={isActive}
            onChange={handleToggleEmojiStatus}
            size="small"
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: "#fff" },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#16a34a",
              },
            }}
          />
        </div>

        <div className="mt-5">
          <img
            src={item.emoji}
            alt={item.name}
            className="h-32 w-28 object-contain"
          />
        </div>
        <div className="mt-2 text-base font-semibold tracking-wide">
          {item.name}
        </div>
        <div className="text-sm tracking-wide opacity-90">
          ₹ {item.coinDeduct}
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={item.isFeatured}
            onChange={handleFeaturedToggle}
            className="accent- h-4 w-4 rounded border-white tracking-wide accent-[#c51369]"
          />
          Show directly in app
        </label>
      </div>

      {/* Modals */}
      <EmojiWarningPopup
        fetchAllEmojis={fetchAllEmojis}
        emojiDetails={item}
        warnPopupOpen={warnPopupOpen}
        handleClose={() => setWarnPopupOpen(false)}
        staffId={staffId}
      />
      <EmojiEditPopup
        fetchAllEmojis={fetchAllEmojis}
        emojiDetails={item}
        editEmojiModalOpen={editEmojiModalOpen}
        handleEmojiModalClose={() => setEditEmojiModalOpen(false)}
        staffId={staffId}
        categories={categories}
      />
    </>
  );
};

// ── Skeleton loaders ──
const GiftsSkeletonLoader = () => (
  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="h-44 animate-pulse rounded-2xl bg-gray-100" />
    ))}
  </div>
);

export default Gifts;
